import { ecorEnv } from '@/../environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, shareReplay, finalize } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class CaseListService {
    unreadMessages = signal<number>(0);
    private caseListComponent: any = null;
    readCaseIds = new Set<string>();

    // request cache/sharing state ---
    private casesByEmail$: Observable<any> | null = null;
    private casesByEmailKey: string | null = null;
    private cacheTimestamp = 0;
    private readonly CACHE_TTL_MS = 3000; // 3 seconds — long enough to absorb race, short enough to stay fresh


    constructor(private httpClient: HttpClient) { }

    getCasesByEmail(email: string): Observable<any> {
        const isCacheValid = 
            this.casesByEmail$ && 
            this.casesByEmailKey === email && 
            (Date.now() - this.cacheTimestamp) < this.CACHE_TTL_MS;
    
        if (isCacheValid) {
            return this.casesByEmail$;
        }
    
        const profilesUrl = ecorEnv.apiEndpoint + '/secure/pega/cases/' + email;
    
        this.casesByEmailKey = email;
        this.cacheTimestamp = Date.now();
        this.casesByEmail$ = this.httpClient.get<any>(profilesUrl)
            .pipe(
                catchError(this.handleError),
                shareReplay({ bufferSize: 1, refCount: false })
            );
    
        return this.casesByEmail$;
    }

    // Call this after any action that changes the case list server-side
    // (e.g. submitting a new case, or when you explicitly want a fresh fetch)
    invalidateCasesCache(): void {
        this.casesByEmail$ = null;
        this.casesByEmailKey = null;
    }

    private handleError = (error: HttpErrorResponse) => {
        console.error('surveys', 'error', error);
        // clear cache on error so a failed request doesn't get replayed forever
        this.casesByEmail$ = null;
        this.casesByEmailKey = null;

        if (error.error instanceof ErrorEvent) {
            console.error('surveys', 'client side error:', error.error.message);
        } else {
            return throwError('Error-111111');
        }
        return throwError('Error-222222');
    }

    // Check if already read
    isCaseRead(caseId: string): boolean {
        return this.readCaseIds.has(caseId);
    }

    // Mark as read and decrement only if not read before
     markCaseRead(caseId: string): void {
        if (!this.readCaseIds.has(caseId)) {
            this.readCaseIds.add(caseId);

            const current = this.unreadMessages();
            if (current > 0) {
                this.unreadMessages.set(current - 1);
            }
        }
    }

     incrementUnread(): void {
        this.unreadMessages.update(v => v + 1);
    }

    setCaseListComponent(component: any) {
        this.caseListComponent = component;
    }

    notifyCaseAccessed(caseId: string) {
        if (this.readCaseIds.has(caseId)) {
            return; // Already read, do not notify
        }
        if (this.caseListComponent && this.caseListComponent.onMessageAccessed) {
            this.caseListComponent.onMessageAccessed(caseId);
        }
    }
}
